import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Container,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FiEye, FiFileText, FiShoppingCart } from 'react-icons/fi';

import AppNavbar from '../components/AppNavbar';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import api from '../api/client';

const OrdersPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { refreshCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reordering, setReordering] = useState(null);
  const fetchingRef = useRef(false);

  const isAdmin = user?.role?.name?.toLowerCase() === 'admin' || user?.role?.name?.toLowerCase() === 'superadmin';

  const fetchOrders = useCallback(async () => {
    if (fetchingRef.current) return; // Prevent duplicate calls
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/orders', { params: { limit: 50 } });
      setOrders(data?.data || []);
    } catch (error) {
      setError(error.message || 'Failed to load orders');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [isAuthenticated, navigate, fetchOrders]);

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      approved: 'success',
      processing: 'info',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const handleViewInvoice = async (orderId) => {
    try {
      const response = await api.get(`/invoices/${orderId}/html`, {
        responseType: 'text',
      });
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(response.data);
        newWindow.document.close();
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to load invoice');
    }
  };

  const handleDownloadInvoice = async (orderId, orderNumber) => {
    try {
      const response = await api.get(`/invoices/${orderId}/download`, {
        responseType: 'blob',
      });
      
      // Check if response is PDF or HTML based on content type
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      const isPDF = contentType.includes('application/pdf');
      const extension = isPDF ? 'pdf' : 'html';
      
      const blob = new Blob([response.data], { type: contentType || (isPDF ? 'application/pdf' : 'text/html') });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderNumber || orderId}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to download invoice');
    }
  };

  const handleReorder = async (order) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setReordering(order._id);
    setError(null);

    try {
      // Use the reorder endpoint
      await api.post('/cart/reorder', {
        orderId: order._id,
      });

      // Refresh cart count
      refreshCart();

      // Navigate to cart page
      navigate('/cart');
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to re-order items');
    } finally {
      setReordering(null);
    }
  };

  if (loading) {
    return (
      <>
        <AppNavbar />
        <Container className="py-5">
          <div className="text-center">
            <Spinner animation="border" role="status" />
            <p className="mt-2 text-muted">Loading orders...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppNavbar />
      <Container className="py-4">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <Card.Title className="mb-0">
                {isAdmin ? 'All Orders' : 'My Orders'}
              </Card.Title>
              <Button variant="outline-primary" size="sm" onClick={fetchOrders}>
                Refresh
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {orders.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No orders found.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Order #</th>
                      {isAdmin && <th>Customer</th>}
                      <th>Date</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id}>
                        <td>{order.orderNumber}</td>
                        {isAdmin && (
                          <td>
                            {order.user?.name || order.user?.email || 'N/A'}
                          </td>
                        )}
                        <td>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td>{order.items?.length || 0}</td>
                        <td>{formatPrice(order.total || 0)}</td>
                        <td>{getStatusBadge(order.status)}</td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => navigate(isAdmin ? `/admin/orders/${order._id}` : `/orders/${order._id}`)}
                              title="View Details"
                            >
                              <FiEye />
                            </Button>
                            {!isAdmin && order.status === 'delivered' && (
                              <Button
                                size="sm"
                                variant="outline-info"
                                onClick={() => handleReorder(order)}
                                title="Re-order"
                                disabled={reordering === order._id}
                              >
                                {reordering === order._id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FiShoppingCart />
                                )}
                              </Button>
                            )}
                            {order.status !== 'pending' && order.status !== 'cancelled' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => handleViewInvoice(order._id)}
                                  title="View Invoice"
                                >
                                  <FiFileText />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleDownloadInvoice(order._id, order.orderNumber)}
                                  title="Download Invoice"
                                >
                                  <FiFileText />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

const OrdersPageWithProtection = () => (
  <ProtectedRoute>
    <OrdersPage />
  </ProtectedRoute>
);

export default OrdersPageWithProtection;

